// You can write more code here

import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import type SequencedMinigameCardLayoutGroup from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/SequencedMinigameCardLayoutGroup';
import type SlottedMinigameCardLayoutGroup from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/SlottedMinigameCardLayoutGroup';
import { waitForMilliseconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { CardDropSiteData } from '@/game/prefabs/StudentGame/CardDropSite.types';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import PlaceholderMinigameCard from '@/game/scenes/PlaceholderStudentGameCard';

export default class CardDropSite extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene, x?: number, y?: number) {
    super(scene, x ?? 0, y ?? 0);
  }

  public backing?: Phaser.GameObjects.NineSlice;
  protected dropSiteCardContainer?: Phaser.GameObjects.Container;
  public layoutGroup:
    | SlottedMinigameCardLayoutGroup
    | SequencedMinigameCardLayoutGroup
    | null = null;
  public zone: Phaser.GameObjects.Zone | null = null;
  public siteName?: string;
  zoneOverrideReference?:
    | Phaser.GameObjects.Image
    | Phaser.GameObjects.NineSlice;
  boardGraphics: Phaser.GameObjects.Rectangle | null = null;
  placeholderCard?: PlaceholderMinigameCard;
  onSizeUpdated?: (width: number, height: number) => void;
  dropSiteData?: CardDropSiteData;
  debugGraphics?: any;

  get DropSiteCardContainer() {
    return this.dropSiteCardContainer;
  }

  init(data: CardDropSiteData) {
    // Initialize the layout group.
    this.siteName = data.siteName;
    this.dropSiteData = data;

    const { backing } = this;
    if (!backing) {
      console.error('no backing for card drop site!', this);
      return;
    }

    // Board zone.

    this.setZone(backing);

    // Responsiveness.
    ResponsivenessManager.InitializeResizeHandler(
      this.scene,
      () => this.handleResize(),
      this,
    );

    this.zone?.setVisible(false); // Turn drop site off while cards are not being dragged.

    return this;
  }

  // Provide an image to define the transform of the drop zone.
  overrideDropZone(newZone: Phaser.GameObjects.Image) {
    this.setZone(newZone);
  }

  setZone(reference: Phaser.GameObjects.Image | Phaser.GameObjects.NineSlice) {
    const { tx, ty } = reference.getWorldTransformMatrix();
    this.zoneOverrideReference = reference;
    if (this.zone) {
      // Remove the old zone.
      this.zone.destroy();
      this.scene.input.off('dragenter', this.handleDragEnter, this);
      this.scene.input.off('dragover', this.handleDragOver, this);
      this.scene.input.off('dragleave', this.handleDragLeave, this);
      this.scene.input.off('drop', this.handleDrop, this);
    }

    this.zone = this.scene.add
      .zone(tx, ty, this.scene.game.scale.width, reference.getBounds().height)
      .setRectangleDropZone(
        this.scene.game.scale.width,
        reference.getBounds().height,
      );

    // For debugging.
    if (this.boardGraphics) {
      this.boardGraphics.destroy();
    }
    // this.boardGraphics = this.scene.add
    //   .rectangle(
    //     this.zone.x,
    //     this.zone.y,
    //     this.zone.getBounds().width,
    //     this.zone.getBounds().height,
    //   )
    //   .setStrokeStyle(2, 0xffff00);

    const data = this.dropSiteData;
    if (!data) {
      console.error('no data for card drop site', this);
      return;
    }
    this.scene.input.on('dragenter', this.handleDragEnter, this);
    this.scene.input.on('dragover', this.handleDragOver, this);
    this.scene.input.on('dragleave', this.handleDragLeave, this);
    this.scene.input.on('drop', this.handleDrop, this);
    reference.setVisible(false);
    this.zone.setVisible(false);
  }

  addCard(
    card: StudentGameCard | PlaceholderMinigameCard,
    index: number,
    realignmentOverride?: 'instant' | 'animated',
  ): boolean {
    const addedCard = this.layoutGroup?.addCard(
      card,
      index,
      realignmentOverride,
    );
    // Update card origin.
    if (card.origin) {
      card.origin.dropSite = this;
      card.origin.index = index;
    }
    return addedCard || false;
  }

  pushCard(
    card: StudentGameCard | PlaceholderMinigameCard,
    realignmentOverride?: 'instant' | 'animated',
  ) {
    this.layoutGroup?.pushCard(card, realignmentOverride);
  }

  removeCard(card: StudentGameCard | PlaceholderMinigameCard) {
    this.layoutGroup?.removeCard(card);
  }

  destroyCard(card: StudentGameCard | PlaceholderMinigameCard) {
    this.layoutGroup?.destroyCard(card);
  }

  transferCard(
    card: StudentGameCard,
    toDropSite: CardDropSite,
    toIndex: number,
  ) {
    const originDropSite = card.origin?.dropSite;
    if (originDropSite) {
      originDropSite.removeCard(card);
    }

    if (toDropSite.layoutGroup!.cards[toIndex] instanceof StudentGameCard) {
      // Special case play the transfer SFX.
      PhaserAudioManager.playSFX(this.scene, 'card-woosh');
    } else {
      PhaserAudioManager.playSFX(this.scene, 'card-drop-sfx');
    }

    toDropSite.addCard(card, toIndex);
  }

  updateMaxCards(newMax: number) {
    if (this.layoutGroup) this.layoutGroup.setMaxCards(newMax);
    this.updateLayout();
  }

  // Displays a potential location to place a card.
  displayCandidatePlacement(_index: number, _card: StudentGameCard) {
    // Override in children.
  }

  removeCandidatePlacement() {
    // Override in children.
  }

  dropCard(card: StudentGameCard, index: number) {
    this.addCard(card, index);
    this.removeCandidatePlacement();
  }

  updateLayout() {
    this.layoutGroup?.updateLayout();
    // Refresh the drop zone.
    if (this.zoneOverrideReference) {
      this.setZone(this.zoneOverrideReference);
    }
  }

  // Feedback effect for all correct cards.
  async riffleCards() {
    const cards = this.layoutGroup!.getMinigameCards();
    const totalTime = 400;
    for (var i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (card instanceof StudentGameCard) {
        card.showCorrectFeedback();
      }
      await waitForMilliseconds(this.scene.time, totalTime / cards.length);
    }
  }

  handleDragEnter(pointer: any, gameObject: any, dropZone: any) {
    if (dropZone !== this.zone) return;
    this.dropSiteData?.onDragEnter(pointer, gameObject, dropZone);
  }

  handleDragOver(pointer: any, gameObject: any, dropZone: any) {
    if (dropZone !== this.zone) return;
    this.dropSiteData?.onDragOver(pointer, gameObject, dropZone);
  }

  handleDragLeave(pointer: any, gameObject: any, dropZone: any) {
    if (dropZone !== this.zone) return;
    this.dropSiteData?.onDragLeave(pointer, gameObject, dropZone);
  }

  handleDrop(pointer: any, gameObject: any, dropZone: any) {
    if (dropZone !== this.zone) return;
    PhaserAudioManager.playSFX(this.scene, 'card-drop-sfx');
    this.dropSiteData?.onDrop(pointer, gameObject, dropZone);
  }

  async handleResize() {
    if (!this.scene || !this.scene.scene.isActive()) return;
    await waitForMilliseconds(this.scene.time, 2);
    this.updateLayout();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */
